import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ResponsiveLine } from '@nivo/line';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

// ── Config por parámetro ──────────────────────────────────────────────────────
const PARAM_CONFIG = {
  TIME:     { label: 'Tiempo',     color: '#818cf8', unit: 's',  format: (v) => { const m = Math.floor(v / 60); const s = v % 60; return m > 0 ? `${m}m ${s}s` : `${s}s`; } },
  REPS:     { label: 'Reps',       color: '#F9BD4E', unit: 'reps', format: (v) => `${v} reps` },
  WEIGHT:   { label: 'Peso',       color: '#f59e0b', unit: 'kg',  format: (v) => `${v} kg`   },
  DISTANCE: { label: 'Distancia',  color: '#34d399', unit: 'km',  format: (v) => `${v} km`   },
};

// ── Agregación por parámetro (igual que en la tabla) ─────────────────────────
function aggregateParam(sets, paramType) {
  if (!sets?.length) return null;
  switch (paramType) {
    case 'TIME':     return sets.reduce((s, x) => s + (x.time_seconds  || 0), 0);
    case 'REPS':     return sets.reduce((s, x) => s + (x.repetitions   || 0), 0);
    case 'DISTANCE': return sets.reduce((s, x) => s + (x.distance_km   || 0), 0);
    case 'WEIGHT': {
      const vals = sets.map((x) => x.weight_kg).filter(Boolean);
      return vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : null;
    }
    default: return null;
  }
}

// ── Tooltip de cada mini-gráfica ──────────────────────────────────────────────
// paramType viene de p.data.paramType (inyectado en cada punto) — referencia estable
function MiniTooltip({ slice }) {
  const p = slice?.points?.[0];
  if (!p) return null;
  const cfg = PARAM_CONFIG[p.data.paramType] || {};
  return (
    <Box sx={{
      background: 'rgba(10,14,35,0.97)',
      border: `1px solid ${cfg.color || '#fff'}44`,
      borderRadius: 1.5,
      p: '6px 10px',
    }}>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block' }}>
        {p.data.xFormatted}
      </Typography>
      <Typography variant="body2" sx={{ color: cfg.color, fontWeight: 700 }}>
        {cfg.format ? cfg.format(p.data.y) : p.data.y}
      </Typography>
    </Box>
  );
}

MiniTooltip.propTypes = {
  slice: PropTypes.shape({
    points: PropTypes.arrayOf(PropTypes.shape({
      data: PropTypes.shape({ xFormatted: PropTypes.string, y: PropTypes.number, paramType: PropTypes.string }),
    })),
  }).isRequired,
};

// ── Gráfica individual por parámetro (en su propia tarjeta) ──────────────────
function ParamChart({ lineData, paramType, chartHeight, tickRotation }) {
  const cfg = PARAM_CONFIG[paramType] || { label: paramType, color: '#F9BD4E' };
  const theme = useTheme();
  const { t } = useTranslation(['performances']);
  const isLight = theme.palette.mode === 'light';
  // A 320 los márgenes fijos de nivo (48 izq + 20 der) se comían un tercio del ancho útil y la
  // gráfica quedaba en una franja ilegible. En xs se aprietan y el área de dibujo respira.
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const chartTheme = useMemo(() => ({
    // Ejes/grilla mode-aware: en claro deben ser OSCUROS (antes blancos → invisibles).
    axis: { ticks: { text: { fill: isLight ? 'rgba(17,21,27,0.6)' : 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 500 } } },
    grid: { line: { stroke: isLight ? 'rgba(17,21,27,0.08)' : 'rgba(255,255,255,0.06)' } },
    crosshair: { line: { stroke: `${cfg.color}66`, strokeWidth: 1, strokeDasharray: '4 4' } },
  }), [cfg.color, isLight]);

  // Inyectar paramType en cada punto para que MiniTooltip lo lea de p.data (referencia estable)
  const enrichedData = useMemo(
    () => lineData.map((d) => ({ ...d, paramType })),
    [lineData, paramType]
  );

  // Valores de cabecera: última marca y mejor marca de la serie.
  const latest = lineData.length ? lineData[lineData.length - 1].y : null;
  const best   = lineData.length ? Math.max(...lineData.map((d) => d.y)) : null;
  const fmt    = (v) => {
    if (v === null) return '—';
    return cfg.format ? cfg.format(v) : v;
  };

  return (
    <Box sx={{
      border: '1px solid', borderColor: 'hairline.main', borderRadius: 2,
      bgcolor: 'surface.raised', p: { xs: 1.25, sm: 2 }, height: '100%',
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.color }} />
          <Typography variant="subtitle2" sx={{ color: cfg.color, fontWeight: 700 }}>
            {cfg.label}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="baseline">
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', lineHeight: 1 }}>
              {t('chart.last', { ns: 'performances', defaultValue: 'última' })}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {fmt(latest)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', lineHeight: 1 }}>
              {t('chart.best', { ns: 'performances', defaultValue: 'mejor' })}
            </Typography>
            <Typography variant="body2" sx={{ color: cfg.color, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {fmt(best)}
            </Typography>
          </Box>
        </Stack>
      </Stack>
      <Box sx={{ height: chartHeight }}>
        <ResponsiveLine
          data={[{ id: cfg.label, data: enrichedData }]}
          colors={[cfg.color]}
          margin={{
            top: 10,
            right: isXs ? 10 : 20,
            bottom: tickRotation !== 0 ? 44 : 30,
            left: isXs ? 30 : 48
          }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
          yFormat={(v) => (cfg.format ? cfg.format(v) : v)}
          xFormat={(v) => v}
          curve="monotoneX"
          lineWidth={2.5}
          enableArea
          areaOpacity={0.1}
          pointSize={8}
          pointColor={cfg.color}
          pointBorderWidth={2}
          pointBorderColor={isLight ? '#FFFFFF' : '#0d111f'}
          enablePointLabel={false}
          enableSlices="x"
          sliceTooltip={MiniTooltip}
          axisTop={null}
          axisRight={null}
          axisLeft={{
            tickSize:    0,
            tickPadding: 8,
            tickValues:  4,
            format:      (v) => {
              if (paramType === 'TIME') { const m = Math.floor(v / 60); const s = v % 60; return m > 0 ? `${m}m` : `${s}s`; }
              return v;
            },
          }}
          axisBottom={{
            tickSize:     0,
            tickPadding:  8,
            tickRotation,
          }}
          enableGridX={false}
          enableGridY
          theme={chartTheme}
          animate
        />
      </Box>
    </Box>
  );
}

ParamChart.propTypes = {
  lineData:     PropTypes.arrayOf(PropTypes.shape({ x: PropTypes.string, y: PropTypes.number })).isRequired,
  paramType:    PropTypes.string.isRequired,
  chartHeight:  PropTypes.number.isRequired,
  tickRotation: PropTypes.number.isRequired,
};

// ── Componente principal ──────────────────────────────────────────────────────
function DynamicPerformanceChart({ performances, params }) {
  const theme    = useTheme();
  const { t }    = useTranslation(['performances']);
  const isMedium = useMediaQuery(theme.breakpoints.up('md'));

  // Sesiones ordenadas cronológicamente (= orden de la tabla)
  const sorted = useMemo(
    () => [...performances].sort((a, b) => new Date(a.period_start_date) - new Date(b.period_start_date)),
    [performances]
  );

  const sessionLabels = useMemo(
    () => sorted.map((p) => format(parseISO(p.period_start_date), 'd MMM', { locale: es })),
    [sorted]
  );

  // Para cada parámetro: [{ x: 'fecha', y: valor }]
  const paramData = useMemo(() =>
    params
      .map((param) => {
        const points = sorted
          .map((perf, i) => {
            const val = aggregateParam(perf.sets, param.parameter_type);
            return val !== null ? { x: sessionLabels[i], y: val } : null;
          })
          .filter(Boolean);
        return { paramType: param.parameter_type, points };
      })
      .filter((d) => d.points.length > 0),
    [params, sorted, sessionLabels]
  );

  const tickRotation = sorted.length > 5 ? -30 : 0;
  const chartHeight  = isMedium ? 190 : 160;
  // Con 2+ parámetros, se reparten en dos columnas en desktop para que cada gráfica respire.
  const twoColumns   = paramData.length > 1;

  if (!paramData.length) return null;

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <Typography variant="caption" sx={{ color: 'text.disabled', mb: 1.5, display: 'block' }}>
        {t('chart.caption', { ns: 'performances', defaultValue: 'Progreso por sesión · cada punto = una sesión de la tabla' })}
      </Typography>
      <Box sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: twoColumns ? '1fr 1fr' : '1fr' },
      }}>
        {paramData.map(({ paramType, points }) => (
          <ParamChart
            key={paramType}
            lineData={points}
            paramType={paramType}
            chartHeight={chartHeight}
            tickRotation={tickRotation}
          />
        ))}
      </Box>
    </Box>
  );
}

DynamicPerformanceChart.propTypes = {
  performances: PropTypes.arrayOf(
    PropTypes.shape({
      period_start_date: PropTypes.string.isRequired,
      sets: PropTypes.arrayOf(PropTypes.shape({
        time_seconds: PropTypes.number,
        repetitions:  PropTypes.number,
        weight_kg:    PropTypes.number,
        distance_km:  PropTypes.number,
      })),
    })
  ).isRequired,
  params: PropTypes.arrayOf(
    PropTypes.shape({ parameter_type: PropTypes.string.isRequired })
  ).isRequired,
};

export default DynamicPerformanceChart;
